import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { FormattedMessage } from 'react-intl';
import {useMutation} from "@tanstack/react-query";
import {useEffect, useRef, useState} from 'react';

import { uuidv4 } from 'features/services/utils/guid';
import {useTrapFocus} from 'components/hooks/useTrapFocus';
import {ChatMessage} from "features/chat/services/ChatService/types";
import {useUiStore, selectChatVisible} from "features/ui/store/useUiStore";
import { getFormattedMessage } from 'features/services/utils/language-helper';
import {ChatServiceQueryMethods} from "features/chat/services/ChatService/query";

import './style.scss';

import { P } from '@atoms/Text/P';
import { Icon } from '@atoms/Media/Icon';
import { A } from '@atoms/LinksAndButtons/A';
import { useAppForm } from 'components/forms/form';
import { Button, ButtonColor, ButtonSize, ButtonState, ButtonType } from '@atoms/LinksAndButtons/Button';
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

export const ChatWidget: React.FunctionComponent = () => {
    const chatEnabled = useUiStore(selectChatVisible);
    const sessionId = useRef(uuidv4());
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [lastUserMessageId, setLastUserMessageId] = useState<string | null>(null);
    const widgetStartRef = useRef<HTMLButtonElement>(null);
    const widgetEndRef = useRef<HTMLButtonElement>(null);
    const toggleWidgetButtonRef = useRef<HTMLButtonElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const lastUserMessageRef = useRef<HTMLDivElement | null>(null);

    const chatForm = useAppForm({
        defaultValues: {
            question: '',
        }
    });

    const {data: messageResponse, isPending, mutate: sendQuestion} = useMutation({
        mutationKey: ['chat', chatForm.state.values.question],
        mutationFn: async (question: string) => {
            return await ChatServiceQueryMethods.sendMessage(question, sessionId.current);
        },
    });

    const closeChat = () => {
        setIsClosing(true);
    };

    const toggleChat = () => {
        if (isOpen) {
            closeChat();
        } else {
            setIsOpen(true);
        }
    };

    const handleWindowAnimationEnd = () => {
        if (isClosing) {
            setIsClosing(false);
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if(messageResponse) {
            setMessages(prev => [...prev, messageResponse]);
        }
    }, [messageResponse]);

    useEffect(() => {
        if (!isOpen) return;

        if(!isPending) return;

        const id = requestAnimationFrame(() => {
            const el = messagesContainerRef.current;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        });

        return () => cancelAnimationFrame(id);
    }, [isPending]);

    useEffect(() => {
        if (!messageResponse || !isOpen) return;

        const target = lastUserMessageRef.current;
        if (!target) return;

        requestAnimationFrame(() => {
            target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
        });
    }, [messageResponse, isOpen]);

    useTrapFocus({
        triggerElementRef: toggleWidgetButtonRef,
        firstFocusElement: widgetStartRef,
        lastFocusElement: widgetEndRef,
        onCloseCallback: toggleChat,
        trigger: isOpen,
        timeout: 350,
    });

    const sendMessage = async (text: string) => {
        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            text: text.trim(),
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setLastUserMessageId(userMessage.id);

        chatForm.setFieldValue('question', '');

        sendQuestion(text);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const question = chatForm.state.values.question.trim();
        if (!question || isPending) return;
        sendMessage(question);
    };

    const renderMessage = (message: ChatMessage) => {
        if (message.sender === 'bot') {
            return (
                <div className="chat-widget__markdown">
                    <ReactMarkdown
                        components={{
                            // Customize markdown components if needed
                            p: ({ children }) => <P>{children}</P>,
                            code: ({ children }) => <pre><code>{children}</code></pre>,
                            ul: ({ children }) => <ul>{children}</ul>,
                            ol: ({ children }) => <ol>{children}</ol>,
                            li: ({ children }) => <li>{children}</li>,
                            strong: ({ children }) => <strong>{children}</strong>,
                            em: ({ children }) => <em>{children}</em>,
                            a: ({ href, children}) => (<A
                                    href={href ? href : ''}
                                    rel="noopener noreferrer"
                                    route={href && href.startsWith('/') ? true : false}
                                    title=''
                                    onClick={closeChat}
                                >{children}</A>),
                            h1: ({ children }) => <Headline level={HeadlineLevel.H5}>{children}</Headline>,
                            h2: ({ children }) => <Headline level={HeadlineLevel.H5}>{children}</Headline>,
                            h4: ({ children }) => <Headline level={HeadlineLevel.H5}>{children}</Headline>,
                            h3: ({ children }) => <Headline level={HeadlineLevel.H5}>{children}</Headline>,
                        }}
                    >
                        {message.text}
                    </ReactMarkdown>
                </div>
            );
        }
        return message.text;
    };

    if (!chatEnabled) return null;

    return (
        <div className={isOpen ? "chat-widget chat-widget--open" : "chat-widget"}>
            <div className='chat-widget__cover' onClick={toggleChat} />
            <Button
                className="chat__button"
                onClick={toggleChat}
                buttonColor={ButtonColor.CTA}
                buttonSize={ButtonSize.DEFAULT}
                buttonType={ButtonType.DEFAULT}
                icon={
                    isOpen
                        ? <Icon name="x" />
                        : <Icon name="contact-bubble" size={32} />
                }
                iconBtn
                aria-label={getFormattedMessage("jm.components.organisms.chatWidget.openChat")}
                ref={toggleWidgetButtonRef}
            >
                <FormattedMessage id="jm.components.organisms.chatWidget.openChat" />
            </Button>

            {/* Chat Window */}
            {(isOpen || isClosing) && (
                <div
                    className={`chat-widget__window${isClosing ? ' chat-widget__window--closing' : ''}`}
                    onAnimationEnd={handleWindowAnimationEnd}
                >
                    <div className="chat-widget__header">
                        <Headline level={HeadlineLevel.H4}><FormattedMessage id="jm.components.organisms.chatWidget.chatName" /></Headline>
                        <Button
                            onClick={toggleChat}
                            buttonColor={ButtonColor.DEFAULT}
                            buttonSize={ButtonSize.DEFAULT}
                            buttonType={ButtonType.NO_STYLE}
                            icon={<Icon name="x" /> }
                            iconBtn
                            aria-label={getFormattedMessage("jm.components.organisms.chatWidget.closeChat")}
                            ref={widgetStartRef}
                        >
                            <FormattedMessage id="jm.components.organisms.chatWidget.closeChat" />
                        </Button>
                    </div>

                    <div className="chat-widget__messages" ref={messagesContainerRef}>
                        <div className="chat-widget__message chat-widget__message--bot chat-widget__welcome">
                            <FormattedMessage id="jm.components.organisms.chatWidget.helloMessage" />
                        </div>

                        {messages.map((message, index) => {
                            const isLastUser = message.sender === 'user' && message.id === lastUserMessageId;
                            return (
                                <div
                                    key= {`message-${index}-${message.id}`}
                                    className={`chat-widget__message chat-widget__message--${message.sender}`}
                                    ref={isLastUser ? lastUserMessageRef : null}
                                >
                                    {renderMessage(message)}
                                </div>
                            );
                        })}

                        {isPending && (
                            <div className="chat-widget__message chat-widget__message--bot chat-widget__message--loading">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        )}
                    </div>

                    <form className="chat-widget__form" onSubmit={handleSubmit}>
                        <chatForm.AppField
                            name="question"
                            children={(field) =>
                                <field.TextField
                                    label={getFormattedMessage('jm.components.organisms.chatWidget.writeAQuestion')}
                                    noValidation
                                />
                            }
                        />
                        <div className='button-wrapper'>
                            <chatForm.Subscribe selector={(state) => state.values.question}>
                                {(question) => (
                                    <Button
                                        buttonColor={ButtonColor.DEFAULT}
                                        buttonSize={ButtonSize.DEFAULT}
                                        buttonType={ButtonType.DEFAULT}
                                        aria-label={getFormattedMessage("jm.components.organisms.chatWidget.send")}
                                        onClick={handleSubmit}
                                        buttonState={!question.trim() || isPending
                                            ? ButtonState.INACTIVE
                                            : ButtonState.DEFAULT
                                        }
                                        ref={widgetEndRef}
                                    >
                                        <FormattedMessage id="jm.components.organisms.chatWidget.send" />
                                    </Button>
                                )}
                            </chatForm.Subscribe>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};


